/**
 * Rastreamento: Meta Pixel + Microsoft Clarity + GA4 (se VITE_GA4_ID).
 *
 * Nenhum script de terceiro carrega no carregamento inicial. Os scripts
 * entram na primeira interação real (scroll, pointerdown, touchstart,
 * keydown) ou quando alguém clica num CTA / envia o formulário.
 * Eventos disparados antes disso ficam numa fila e são enviados, na ordem,
 * logo depois do init + PageView. Nenhum evento se perde.
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

const PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined) || "728950902954412";
const CLARITY_ID = "wj12udvef0";
const GA4_ID = import.meta.env.VITE_GA4_ID as string | undefined;

type Cmd = () => void;
const queue: Cmd[] = [];
let ready = false;
let listening = false;

const INTERACTION_EVENTS = ["scroll", "pointerdown", "touchstart", "keydown"] as const;

function addScript(src: string) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

/** Injeta os scripts de terceiros e esvazia a fila. Idempotente. */
export function loadAnalytics() {
  if (ready || typeof window === "undefined") return;
  ready = true;
  removeListeners();

  // Meta Pixel — stub oficial (guarda chamadas até o fbevents.js chegar)
  if (!window.fbq) {
    const n: any = function (...args: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
    addScript("https://connect.facebook.net/en_US/fbevents.js");
  }
  window.fbq!("init", PIXEL_ID);
  window.fbq!("track", "PageView");

  // Microsoft Clarity
  if (!window.clarity) {
    const c: any = function (...args: unknown[]) {
      (c.q = c.q || []).push(args);
    };
    window.clarity = c;
    addScript(`https://www.clarity.ms/tag/${CLARITY_ID}`);
  }

  // GA4 (opcional). O config já envia o page_view.
  if (GA4_ID && !window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID);
    addScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
  }

  while (queue.length) {
    try {
      queue.shift()!();
    } catch {
      /* ignora falha de um evento isolado */
    }
  }
}

function onFirstInteraction() {
  loadAnalytics();
}

function removeListeners() {
  if (!listening) return;
  listening = false;
  for (const ev of INTERACTION_EVENTS) window.removeEventListener(ev, onFirstInteraction);
}

/** Arma o carregamento na primeira interação real. Chamar num useEffect. */
export function initAnalytics() {
  if (typeof window === "undefined" || ready || listening) return;
  listening = true;
  for (const ev of INTERACTION_EVENTS) {
    window.addEventListener(ev, onFirstInteraction, { passive: true, once: true });
  }
}

function run(cmd: Cmd, forceLoad = false) {
  if (typeof window === "undefined") return;
  if (ready) {
    cmd();
    return;
  }
  queue.push(cmd);
  if (forceLoad) loadAnalytics();
}

export function trackLead(data?: Record<string, unknown>) {
  run(() => {
    window.fbq?.("track", "Lead", data);
    window.gtag?.("event", "generate_lead", data);
  }, true);
}

export function trackInitiateCheckout(data?: Record<string, unknown>) {
  run(() => {
    window.fbq?.("track", "InitiateCheckout", data);
    window.gtag?.("event", "begin_checkout", data);
  }, true);
}

export function trackPurchase(data?: Record<string, unknown>) {
  run(() => {
    window.fbq?.("track", "Purchase", { value: 6870, currency: "BRL", ...data });
    window.gtag?.("event", "purchase", { value: 6870, currency: "BRL", ...data });
  });
}

/** Evento genérico: GA4 nativo + Meta como custom event. */
function track(event: string, data?: Record<string, unknown>, forceLoad = false) {
  run(() => {
    window.gtag?.("event", event, data);
    window.fbq?.("trackCustom", event, data);
  }, forceLoad);
}

/** Formulário de inscrição entrou na tela (uma vez por visita). */
export function trackViewForm() {
  track("view_form", { section: "inscricao" });
  run(() => {
    window.fbq?.("track", "ViewContent", {
      content_name: "Formulário de inscrição",
    });
  });
}

/** Clique em qualquer CTA que leva ao formulário. */
export function trackCtaClick(source: string, label: string) {
  track("click_cta", { cta_source: source, cta_label: label }, true);
}

/** Usuário acionou o envio do formulário. */
export function trackFormAttempt() {
  track("form_submit_attempt", undefined, true);
}

/** Inscrição gravada com sucesso. */
export function trackFormSuccess(data?: Record<string, unknown>) {
  track("form_submit_success", data, true);
}

/** Falha no envio — reason: validation | server | network. */
export function trackFormError(reason: string, detail?: string) {
  track("form_submit_error", { reason, detail }, true);
}

/** Clique no botão de pagamento na página de obrigado. */
export function trackPaymentClick() {
  track("click_payment", { value: 6870, currency: "BRL" }, true);
}
